import React from "react";
import { ChevronDown, ListFilter, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { HotelCountRangeFilter } from "@/components/pages/hotels/components/HotelCountRangeFilter";
import { PmsType } from "@/types/hotel";
import type { SubscriptionPlan } from "@/types/subscription-plan";
import { cn } from "@/lib/utils";
import { PMS_PROVIDER_LABEL_KEYS } from "@/utils/pmsProviderLabels";

export type HotelStatusFilter = "all" | "active" | "inactive";
export type HotelPmsFilter = "all" | PmsType;

export interface HotelsFilterValues {
  status: HotelStatusFilter;
  pms: HotelPmsFilter;
  roomsMin: string;
  roomsMax: string;
  employeesMin: string;
  employeesMax: string;
  billingPlanId: string;
}

interface HotelsFiltersPopoverProps {
  applied: HotelsFilterValues;
  billingPlans: SubscriptionPlan[];
  isBillingPlanFilterDisabled: boolean;
  onApply: (filters: HotelsFilterValues) => void;
}

type FilterSectionId = "status" | "pms" | "rooms" | "employees" | "billingPlan";

export const DEFAULT_HOTELS_FILTERS: HotelsFilterValues = {
  status: "all",
  pms: "all",
  roomsMin: "",
  roomsMax: "",
  employeesMin: "",
  employeesMax: "",
  billingPlanId: "all",
};

const FILTER_SECTION_TRIGGER_CLASS =
  "flex h-9 w-full items-center justify-between gap-2 rounded-md border border-base-border bg-white px-4 py-2 text-left text-sm font-medium leading-normal text-black/45 outline-none transition-colors hover:bg-secondary-badge focus-visible:ring-0";

const FILTER_SECTION_PANEL_CLASS =
  "rounded-md border border-base-border bg-white p-2 shadow-sm";

const isInvalidCountRange = (min: string, max: string): boolean =>
  min.length > 0 && max.length > 0 && Number(min) > Number(max);

const isHotelStatusFilter = (value: string): value is HotelStatusFilter =>
  value === "all" || value === "active" || value === "inactive";

const isHotelPmsFilter = (value: string): value is HotelPmsFilter =>
  value === "all" || Object.values(PmsType).some((pms) => pms === value);

const getActiveFiltersCount = (filters: HotelsFilterValues): number =>
  [
    filters.status !== "all",
    filters.pms !== "all",
    filters.roomsMin.length > 0 || filters.roomsMax.length > 0,
    filters.employeesMin.length > 0 || filters.employeesMax.length > 0,
    filters.billingPlanId !== "all",
  ].filter(Boolean).length;

const getFiltersTitle = (
  activeCount: number,
  defaultTitle: string,
  titleWithCount: string,
): string => (activeCount > 0 ? titleWithCount : defaultTitle);

const getInitiallyExpandedSection = (
  filters: HotelsFilterValues,
): FilterSectionId => {
  const sectionStates: Array<{
    id: FilterSectionId;
    isActive: boolean;
  }> = [
    { id: "status", isActive: filters.status !== "all" },
    { id: "pms", isActive: filters.pms !== "all" },
    {
      id: "rooms",
      isActive: Boolean(filters.roomsMin || filters.roomsMax),
    },
    {
      id: "employees",
      isActive: Boolean(filters.employeesMin || filters.employeesMax),
    },
    {
      id: "billingPlan",
      isActive: filters.billingPlanId !== "all",
    },
  ];

  return sectionStates.find(({ isActive }) => isActive)?.id ?? "status";
};

export const HotelsFiltersPopover: React.FC<HotelsFiltersPopoverProps> = ({
  applied,
  billingPlans,
  isBillingPlanFilterDisabled,
  onApply,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState<HotelsFilterValues>(applied);
  const [expandedSection, setExpandedSection] =
    React.useState<FilterSectionId | null>("status");

  const roomsRangeInvalid = isInvalidCountRange(draft.roomsMin, draft.roomsMax);
  const employeesRangeInvalid = isInvalidCountRange(
    draft.employeesMin,
    draft.employeesMax,
  );
  const activeCount = getActiveFiltersCount(draft);
  const filtersTitle = getFiltersTitle(
    activeCount,
    t("hotels.page.filters.title"),
    t("hotels.page.filters.titleWithCount", { count: activeCount }),
  );

  const toggleSection = (section: FilterSectionId): void => {
    setExpandedSection((previous) => (previous === section ? null : section));
  };

  const handleOpenChange = (nextOpen: boolean): void => {
    setOpen(nextOpen);
    setDraft(applied);
    setExpandedSection(getInitiallyExpandedSection(applied));
  };

  const handleReset = (): void => {
    setDraft(DEFAULT_HOTELS_FILTERS);
    setExpandedSection("status");
    onApply(DEFAULT_HOTELS_FILTERS);
  };

  const handleApply = (): void => {
    onApply(draft);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="icon-text"
          className="text-foreground border-base-border h-8 min-h-8 w-fit gap-2 border py-1 text-sm font-medium shadow-xs"
        >
          <ListFilter />
          {t("hotels.page.filters.button")}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={8}
        className="border-base-border flex max-h-[min(560px,calc(100vh-120px))] w-[320px] max-w-[calc(100vw-2rem)] flex-col gap-6 overflow-hidden rounded-2xl border bg-white p-4 shadow-md"
      >
        <div className="flex shrink-0 items-start justify-between gap-2">
          <h4 className="text-base leading-normal font-semibold text-black/96">
            {filtersTitle}
          </h4>
          <Button
            type="button"
            variant="span"
            className="hover:bg-secondary-badge rounded p-1 text-black/45 transition hover:text-black/96"
            onClick={() => setOpen(false)}
            aria-label={t("hotels.page.filters.close")}
          >
            <X className="size-5" />
          </Button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto pr-0.5">
          <div className="flex flex-col gap-2">
            <Button
              type="button"
              variant="span"
              className={FILTER_SECTION_TRIGGER_CLASS}
              onClick={() => toggleSection("status")}
              aria-expanded={expandedSection === "status"}
            >
              {t("hotels.page.statusFilter")}
              <ChevronDown
                className={cn(
                  "shrink-0 text-black/45 transition-transform",
                  expandedSection === "status" && "rotate-180",
                )}
              />
            </Button>
            {expandedSection === "status" ? (
              <div className={FILTER_SECTION_PANEL_CLASS}>
                <Select
                  value={draft.status}
                  onValueChange={(status) => {
                    if (isHotelStatusFilter(status)) {
                      setDraft((previous) => ({ ...previous, status }));
                    }
                  }}
                >
                  <SelectTrigger size="sm" className="w-full justify-between">
                    <SelectValue placeholder={t("hotels.page.statusFilter")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="all">
                        {t("hotels.page.allStatuses")}
                      </SelectItem>
                      <SelectItem value="active">
                        {t("hotels.status.active")}
                      </SelectItem>
                      <SelectItem value="inactive">
                        {t("hotels.status.inactive")}
                      </SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            ) : null}

            <Button
              type="button"
              variant="span"
              className={FILTER_SECTION_TRIGGER_CLASS}
              onClick={() => toggleSection("pms")}
              aria-expanded={expandedSection === "pms"}
            >
              {t("hotels.page.pmsFilter")}
              <ChevronDown
                className={cn(
                  "shrink-0 text-black/45 transition-transform",
                  expandedSection === "pms" && "rotate-180",
                )}
              />
            </Button>
            {expandedSection === "pms" ? (
              <div className={FILTER_SECTION_PANEL_CLASS}>
                <Select
                  value={draft.pms}
                  onValueChange={(pms) => {
                    if (isHotelPmsFilter(pms)) {
                      setDraft((previous) => ({ ...previous, pms }));
                    }
                  }}
                >
                  <SelectTrigger size="sm" className="w-full justify-between">
                    <SelectValue placeholder={t("hotels.page.pmsFilter")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="all">
                        {t("hotels.page.allPms")}
                      </SelectItem>
                      {Object.values(PmsType).map((pms) => (
                        <SelectItem key={pms} value={pms}>
                          {t(PMS_PROVIDER_LABEL_KEYS[pms])}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            ) : null}

            <Button
              type="button"
              variant="span"
              className={FILTER_SECTION_TRIGGER_CLASS}
              onClick={() => toggleSection("rooms")}
              aria-expanded={expandedSection === "rooms"}
            >
              {t("hotels.page.roomsFilter")}
              <ChevronDown
                className={cn(
                  "shrink-0 text-black/45 transition-transform",
                  expandedSection === "rooms" && "rotate-180",
                )}
              />
            </Button>
            {expandedSection === "rooms" ? (
              <div className={FILTER_SECTION_PANEL_CLASS}>
                <HotelCountRangeFilter
                  label={t("hotels.page.roomsFilter")}
                  minPlaceholder={t("hotels.page.roomsMinFilter")}
                  maxPlaceholder={t("hotels.page.roomsMaxFilter")}
                  minValue={draft.roomsMin}
                  maxValue={draft.roomsMax}
                  onMinChange={(roomsMin) =>
                    setDraft((previous) => ({ ...previous, roomsMin }))
                  }
                  onMaxChange={(roomsMax) =>
                    setDraft((previous) => ({ ...previous, roomsMax }))
                  }
                  errorMessage={
                    roomsRangeInvalid
                      ? t("hotels.page.invalidRoomsRange")
                      : undefined
                  }
                />
              </div>
            ) : null}

            <Button
              type="button"
              variant="span"
              className={FILTER_SECTION_TRIGGER_CLASS}
              onClick={() => toggleSection("employees")}
              aria-expanded={expandedSection === "employees"}
            >
              {t("hotels.page.employeesFilter")}
              <ChevronDown
                className={cn(
                  "shrink-0 text-black/45 transition-transform",
                  expandedSection === "employees" && "rotate-180",
                )}
              />
            </Button>
            {expandedSection === "employees" ? (
              <div className={FILTER_SECTION_PANEL_CLASS}>
                <HotelCountRangeFilter
                  label={t("hotels.page.employeesFilter")}
                  minPlaceholder={t("hotels.page.employeesMinFilter")}
                  maxPlaceholder={t("hotels.page.employeesMaxFilter")}
                  minValue={draft.employeesMin}
                  maxValue={draft.employeesMax}
                  onMinChange={(employeesMin) =>
                    setDraft((previous) => ({ ...previous, employeesMin }))
                  }
                  onMaxChange={(employeesMax) =>
                    setDraft((previous) => ({ ...previous, employeesMax }))
                  }
                  errorMessage={
                    employeesRangeInvalid
                      ? t("hotels.page.invalidEmployeesRange")
                      : undefined
                  }
                />
              </div>
            ) : null}

            <Button
              type="button"
              variant="span"
              className={FILTER_SECTION_TRIGGER_CLASS}
              onClick={() => toggleSection("billingPlan")}
              aria-expanded={expandedSection === "billingPlan"}
              disabled={isBillingPlanFilterDisabled}
            >
              {t("hotels.page.billingPlanFilter")}
              <ChevronDown
                className={cn(
                  "shrink-0 text-black/45 transition-transform",
                  expandedSection === "billingPlan" && "rotate-180",
                )}
              />
            </Button>
            {expandedSection === "billingPlan" &&
            !isBillingPlanFilterDisabled ? (
              <div className={FILTER_SECTION_PANEL_CLASS}>
                <Select
                  value={draft.billingPlanId}
                  onValueChange={(billingPlanId) =>
                    setDraft((previous) => ({
                      ...previous,
                      billingPlanId,
                    }))
                  }
                >
                  <SelectTrigger size="sm" className="w-full justify-between">
                    <SelectValue
                      placeholder={t("hotels.page.billingPlanFilter")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="all">
                        {t("hotels.page.allBillingPlans")}
                      </SelectItem>
                      {billingPlans.map((plan) => (
                        <SelectItem key={plan.id} value={plan.id}>
                          {plan.title}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            ) : null}
          </div>
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2">
          <Button type="button" variant="new_outline" onClick={handleReset}>
            {t("hotels.page.filters.reset")}
          </Button>
          <Button
            type="button"
            variant="new_default"
            onClick={handleApply}
            disabled={roomsRangeInvalid || employeesRangeInvalid}
          >
            {t("hotels.page.filters.apply")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
